import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { STAGES, SOURCES, PRIORITIES, INDIAN_STATES, INTERESTS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Sel, Field } from "./Sel";

const EMPTY = { name: "", company: "", email: "", phone: "", city: "", state: "", interested_for: "", source: "Website", service: "", status: "new", priority: "medium", assigned_to: null, notes: "", tags: [] };

export const LeadFormDialog = ({ open, onOpenChange, lead, onSaved }) => {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [reps, setReps] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF(lead ? { ...EMPTY, ...lead } : EMPTY);
    if (open && user.role === "admin") api.get("/users").then((r) => setReps(r.data.filter((u) => u.role === "sales" || u.role === "admin")));
  }, [open, lead, user.role]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Lead name is required");
    setBusy(true);
    try {
      const body = { ...f };
      const { data } = lead ? await api.put(`/leads/${lead.id}`, body) : await api.post("/leads", body);
      toast.success(lead ? "Lead updated" : "Lead created");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader><DialogTitle>{lead ? "Edit lead" : "New lead"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Contact name *"><Input data-testid="lead-name-input" value={f.name} onChange={set("name")} /></Field>
          <Field label="Company"><Input data-testid="lead-company-input" value={f.company} onChange={set("company")} /></Field>
          <Field label="Email"><Input data-testid="lead-email-input" value={f.email} onChange={set("email")} /></Field>
          <Field label="Phone"><Input data-testid="lead-phone-input" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="City"><Input data-testid="lead-city-input" value={f.city} onChange={set("city")} /></Field>
          <Field label="State"><Sel testid="lead-state-select" value={f.state} onChange={set("state")} options={INDIAN_STATES} placeholder="Select state" /></Field>
          <Field label="Interested for"><Sel testid="lead-interest-select" value={f.interested_for} onChange={set("interested_for")} options={INTERESTS} placeholder="Select interest" /></Field>
          <Field label="Product / Service"><Input data-testid="lead-service-input" value={f.service} onChange={set("service")} /></Field>
          <Field label="Source"><Sel testid="lead-source-select" value={f.source} onChange={set("source")} options={SOURCES} /></Field>
          <Field label="Stage"><Sel testid="lead-status-select" value={f.status} onChange={set("status")} options={STAGES.map((s) => ({ value: s.key, label: s.label }))} /></Field>
          <Field label="Priority"><Sel testid="lead-priority-select" value={f.priority} onChange={set("priority")} options={PRIORITIES} /></Field>
          {user.role === "admin" && (
            <Field label="Assign to"><Sel testid="lead-assign-select" value={f.assigned_to} onChange={set("assigned_to")} placeholder="Select employee" options={reps.map((r) => ({ value: r.id, label: `${r.name} (${r.role})` }))} /></Field>
          )}
          <div className="sm:col-span-2"><Field label="Requirement notes"><Textarea data-testid="lead-notes-input" rows={3} value={f.notes} onChange={set("notes")} /></Field></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-form-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lead-form-save-btn">{busy ? "Saving..." : "Save lead"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
